import type { ImageSet, PublicProfile } from "@pax/api-contract/common";
import { INPUT_WORDS } from "@pax/moderation-copy";
import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  focusHaloFlush,
  HapticSurface,
  panelMotion,
  press,
} from "@pax/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ImageUp, LoaderCircle, Pencil, Trash2 } from "lucide-react";
import { type DragEvent, type ReactNode, type RefObject, useEffect, useRef, useState } from "react";
import { ProfileAppeals } from "@/features/moderation/ItemAppeals";
import { isModerationError } from "@/features/moderation/refusals";
import { SubmitError } from "@/features/moderation/SubmitError";
import { api, orpc, reachable } from "@/lib/api";
import { srcSet } from "@/lib/images";
import { CENTRED_CROP, type Crop, type LocalImage, openLocalImage } from "../image-crop";
import {
  changeProfileImage,
  IMAGE_FILE_ACCEPT,
  imageChangeFailure,
  type ProfileImageKind,
  unusableImageFile,
} from "../profile-images";
import { AccountAvatar } from "./AccountAvatar";
import { CroppedImage, ImageCropper } from "./ImageCropper";
import { ProfileBanner } from "./ProfileBanner";

type ProfileImages = Pick<
  PublicProfile,
  "profileId" | "displayName" | "username" | "avatar" | "banner"
>;

/** Each kind's shape, as width over height. */
const ASPECTS = { avatar: 1, banner: 3 } as const;

const PREVIEW_AVATAR_SIZE = 88;

/** How wide each kind is drawn in the preview, which picks the file the browser loads. */
const PREVIEW_SIZES = {
  avatar: `${PREVIEW_AVATAR_SIZE}px`,
  banner: "(width < 480px) calc(100vw - 72px), 408px",
} as const;

/** A picked file, and the part of it to keep. */
interface CroppedFile {
  file: File;
  image: LocalImage;
  crop: Crop;
}

type ImageChange =
  | { phase: "idle" }
  | { phase: "uploading"; picked: CroppedFile; fraction: number }
  | { phase: "processing"; picked: CroppedFile }
  | { phase: "done"; picked: CroppedFile; image: ImageSet | null }
  | { phase: "failed"; message: string; retry: CroppedFile | null; error?: unknown };

const PRELOAD_TIMEOUT_MS = 5000;

/** Two image sets are the same image when they share a file, since every file's URL names its upload. */
function sameImage(left: ImageSet | null, right: ImageSet | null): boolean {
  return left?.sources[0].url === right?.sources[0].url;
}

/** Downloads the file the preview will show before it is shown, so switching to it doesn't flash. */
async function preloadImage(image: ImageSet, sizes: string): Promise<void> {
  const element = new Image();
  element.sizes = sizes;
  element.srcset = srcSet(image);
  await Promise.race([
    element.decode().catch(() => undefined),
    new Promise(resolve => setTimeout(resolve, PRELOAD_TIMEOUT_MS)),
  ]);
}

/**
 * One image's change: what it shows while the change runs, and how it ended.
 * It lives with the dialog rather than inside it, so closing the dialog in the
 * middle of a change and opening it again shows the same change.
 */
function useImageChange(kind: ProfileImageKind, profileId: string) {
  const queryClient = useQueryClient();
  const profileKey = orpc.profiles.get.queryKey({ input: { params: { profileId } } });
  const [change, setChange] = useState<ImageChange>({ phase: "idle" });
  const ownedUrl = useRef<string | null>(null);

  useEffect(
    () => () => {
      if (ownedUrl.current) URL.revokeObjectURL(ownedUrl.current);
    },
    [],
  );

  const removal = useMutation({
    mutationFn: () => reachable(api.profiles.images.delete({ params: { profileId, kind } })),
    onMutate: () => setChange({ phase: "idle" }),
    onSuccess: response => queryClient.setQueryData(profileKey, response),
  });

  function refuse(message: string) {
    removal.reset();
    setChange({ phase: "failed", message, retry: null });
  }

  async function send(picked: CroppedFile) {
    removal.reset();
    if (ownedUrl.current && ownedUrl.current !== picked.image.url) {
      URL.revokeObjectURL(ownedUrl.current);
    }
    ownedUrl.current = picked.image.url;
    setChange({ phase: "uploading", picked, fraction: 0 });

    try {
      const response = await changeProfileImage({
        profileId,
        kind,
        file: picked.file,
        crop: picked.crop,
        onStep: step =>
          setChange(
            step.phase === "uploading"
              ? { phase: "uploading", picked, fraction: step.fraction }
              : { phase: "processing", picked },
          ),
      });
      const image = response.profile[kind];
      if (image) await preloadImage(image, PREVIEW_SIZES[kind]);
      queryClient.setQueryData(profileKey, response);
      setChange({ phase: "done", picked, image });
    } catch (error) {
      const failure = imageChangeFailure(error);
      setChange({
        phase: "failed",
        message: failure.message,
        retry: failure.retryable ? picked : null,
        error,
      });
    }
  }

  const busy = change.phase === "uploading" || change.phase === "processing" || removal.isPending;

  /** The picked image to show instead of the profile's current one, until the new one is in place. */
  function shownPick(current: ImageSet | null): CroppedFile | null {
    if (change.phase === "uploading" || change.phase === "processing") return change.picked;
    if (change.phase === "done" && !sameImage(change.image, current)) return change.picked;
    return null;
  }

  return { change, send, refuse, removal, busy, shownPick };
}

type ImageChangeState = ReturnType<typeof useImageChange>;

function progressLabel(state: ImageChangeState): string | null {
  const { change, removal } = state;
  if (change.phase === "uploading") return `Uploading ${Math.round(change.fraction * 100)}%`;
  if (change.phase === "processing") return "Processing…";
  if (removal.isPending) return "Removing…";
  return null;
}

/** Takes a file dropped on an element; `over` says whether one is being held there. */
function useFileDrop(onFile: (file: File) => void, disabled: boolean) {
  const [over, setOver] = useState(false);
  return {
    over,
    handlers: {
      onDragOver(event: DragEvent) {
        event.preventDefault();
        event.dataTransfer.dropEffect = disabled ? "none" : "copy";
        if (!disabled) setOver(true);
      },
      onDragLeave() {
        setOver(false);
      },
      onDrop(event: DragEvent) {
        event.preventDefault();
        setOver(false);
        const file = event.dataTransfer.files[0];
        if (file && !disabled) onFile(file);
      },
    },
  };
}

/** A missed drop would open the file in the tab and leave the app, so it does nothing instead. */
function useIgnoredStrayDrops(active: boolean) {
  useEffect(() => {
    if (!active) return;
    function refuse(event: globalThis.DragEvent) {
      if (event.defaultPrevented) return;
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = "none";
    }
    function ignore(event: globalThis.DragEvent) {
      event.preventDefault();
    }
    window.addEventListener("dragover", refuse);
    window.addEventListener("drop", ignore);
    return () => {
      window.removeEventListener("dragover", refuse);
      window.removeEventListener("drop", ignore);
    };
  }, [active]);
}

const editButton = cn(
  "relative grid size-8 place-items-center rounded-full bg-background/80 text-foreground shadow-sm ring-1 ring-foreground/10 hover:bg-background",
  panelMotion,
  press,
  focusHaloFlush,
);

/**
 * The pencil on an image. With an image it offers to replace or remove it; without one it
 * opens the file picker straight away.
 */
function EditImage({
  kind,
  hasImage,
  onPick,
  onRemove,
  className,
}: {
  kind: ProfileImageKind;
  hasImage: boolean;
  onPick: () => void;
  onRemove: () => void;
  className: string;
}) {
  if (!hasImage) {
    return (
      <button
        type="button"
        aria-label={`Add ${kind}`}
        title={`Add ${kind}`}
        onClick={onPick}
        className={cn(editButton, className)}
      >
        <Pencil aria-hidden className="size-4" />
        <HapticSurface />
      </button>
    );
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={`Edit ${kind}`}
        title={`Edit ${kind}`}
        className={cn(editButton, className)}
      >
        <Pencil aria-hidden className="size-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuItem onClick={onPick}>
          <ImageUp aria-hidden />
          Replace {kind}
        </DropdownMenuItem>
        <DropdownMenuItem variant="destructive" onClick={onRemove}>
          <Trash2 aria-hidden />
          Remove {kind}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * One image in the preview, veiled while it changes. An image dropped on it is picked like one
 * from the file picker.
 */
function ImageFrame({
  kind,
  state,
  onFile,
  className,
  children,
}: {
  kind: ProfileImageKind;
  state: ImageChangeState;
  onFile: (file: File) => void;
  className: string;
  children: ReactNode;
}) {
  const drop = useFileDrop(onFile, state.busy);
  const progress = progressLabel(state);
  const small = kind === "avatar";

  return (
    <div {...drop.handlers} className={cn("relative overflow-hidden", className)}>
      {children}
      <span
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-0 rounded-[inherit]",
          panelMotion,
          drop.over && "bg-foreground/15 ring-2 ring-foreground/40 ring-inset",
        )}
      />
      {progress ? (
        <span className="absolute inset-0 grid place-items-center bg-background/60 text-xs font-medium">
          <span role="status" className="flex items-center gap-1.5">
            <LoaderCircle aria-hidden className="size-4 animate-spin" />
            <span className={cn("tabular-nums", small && "sr-only")}>{progress}</span>
          </span>
        </span>
      ) : null}
    </div>
  );
}

function FileInput({
  input,
  onFile,
}: {
  input: RefObject<HTMLInputElement | null>;
  onFile: (file: File) => void;
}) {
  return (
    <input
      ref={input}
      type="file"
      accept={IMAGE_FILE_ACCEPT}
      tabIndex={-1}
      aria-hidden
      className="sr-only"
      onChange={event => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (file) onFile(file);
      }}
    />
  );
}

function ImageError({ kind, state }: { kind: ProfileImageKind; state: ImageChangeState }) {
  const { change, removal } = state;
  if (change.phase === "failed" && isModerationError(change.error)) {
    const inputs = INPUT_WORDS[kind === "avatar" ? "profile_avatar" : "profile_banner"].inputs;
    return <SubmitError error={change.error} inputs={inputs} />;
  }
  const message = removal.isError
    ? `Couldn't remove your ${kind}. Try again.`
    : change.phase === "failed"
      ? change.message
      : null;
  if (!message) return null;
  const retry = change.phase === "failed" ? change.retry : null;

  return (
    <p
      role="alert"
      className="flex items-center gap-3 rounded-lg bg-destructive/10 px-3 py-2 text-destructive"
    >
      <span className="min-w-0 flex-1 text-pretty">{message}</span>
      {retry ? (
        <button
          type="button"
          onClick={() => void state.send(retry)}
          className={cn("shrink-0 rounded-md font-medium hover:underline", focusHaloFlush)}
        >
          Try again
        </button>
      ) : null}
    </p>
  );
}

function ProfileImagesPreview({
  profile,
  avatar,
  banner,
  onFile,
}: {
  profile: ProfileImages;
  avatar: ImageChangeState;
  banner: ImageChangeState;
  onFile: (kind: ProfileImageKind, file: File) => void;
}) {
  const avatarInput = useRef<HTMLInputElement>(null);
  const bannerInput = useRef<HTMLInputElement>(null);
  const avatarPick = avatar.shownPick(profile.avatar);
  const bannerPick = banner.shownPick(profile.banner);
  const pickAvatar = () => avatarInput.current?.click();
  const pickBanner = () => bannerInput.current?.click();

  return (
    <>
      <div className="relative pb-12">
        <ImageFrame
          kind="banner"
          state={banner}
          onFile={file => onFile("banner", file)}
          className="aspect-[3/1] w-full rounded-xl bg-foreground/5"
        >
          {bannerPick ? (
            <CroppedImage
              image={bannerPick.image}
              crop={bannerPick.crop}
              frameAspect={ASPECTS.banner}
            />
          ) : profile.banner ? (
            <ProfileBanner banner={profile.banner} sizes={PREVIEW_SIZES.banner} />
          ) : null}
        </ImageFrame>
        {banner.busy ? null : (
          <EditImage
            kind="banner"
            hasImage={profile.banner !== null}
            onPick={pickBanner}
            onRemove={() => banner.removal.mutate()}
            className="absolute top-2 right-2"
          />
        )}

        <div className="absolute bottom-0 left-4 flex items-end gap-3">
          <span className="relative shrink-0 rounded-full bg-popover p-1">
            <ImageFrame
              kind="avatar"
              state={avatar}
              onFile={file => onFile("avatar", file)}
              className="size-22 rounded-full"
            >
              {avatarPick ? (
                <CroppedImage
                  image={avatarPick.image}
                  crop={avatarPick.crop}
                  frameAspect={ASPECTS.avatar}
                />
              ) : (
                <AccountAvatar
                  name={profile.displayName}
                  image={profile.avatar}
                  size={PREVIEW_AVATAR_SIZE}
                  fallbackClassName="text-2xl"
                />
              )}
            </ImageFrame>
            {avatar.busy ? null : (
              <EditImage
                kind="avatar"
                hasImage={profile.avatar !== null}
                onPick={pickAvatar}
                onRemove={() => avatar.removal.mutate()}
                className="absolute top-0 right-0"
              />
            )}
          </span>
          <div className="min-w-0 pb-2">
            <p className="truncate text-sm font-medium">{profile.displayName}</p>
            {profile.username ? (
              <p className="truncate text-xs text-muted-foreground">@{profile.username}</p>
            ) : null}
          </div>
        </div>
      </div>

      <ImageError kind="avatar" state={avatar} />
      <ImageError kind="banner" state={banner} />

      <FileInput input={avatarInput} onFile={file => onFile("avatar", file)} />
      <FileInput input={bannerInput} onFile={file => onFile("banner", file)} />
    </>
  );
}

/** Where you change the avatar and banner everyone else sees, and pick the part of each to show. */
export function ProfileImagesDialog({
  profile,
  open,
  onOpenChange,
}: {
  profile: ProfileImages;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const avatar = useImageChange("avatar", profile.profileId);
  const banner = useImageChange("banner", profile.profileId);
  const [cropping, setCropping] = useState<(CroppedFile & { kind: ProfileImageKind }) | null>(null);
  useIgnoredStrayDrops(open);

  const changeOf = (kind: ProfileImageKind) => (kind === "avatar" ? avatar : banner);

  function discardCropping() {
    if (cropping) URL.revokeObjectURL(cropping.image.url);
    setCropping(null);
  }

  async function pick(kind: ProfileImageKind, file: File) {
    const unusable = unusableImageFile(file);
    if (unusable) {
      changeOf(kind).refuse(unusable);
      return;
    }
    const image = await openLocalImage(file);
    if (!image) {
      changeOf(kind).refuse("That image can't be opened. Try another.");
      return;
    }
    discardCropping();
    setCropping({ kind, file, image, crop: CENTRED_CROP });
  }

  function apply() {
    if (!cropping) return;
    const { kind, ...picked } = cropping;
    setCropping(null);
    void changeOf(kind).send(picked);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={nextOpen => {
        if (!nextOpen) discardCropping();
        onOpenChange(nextOpen);
      }}
    >
      <DialogContent className="max-w-md">
        {cropping ? (
          <>
            <DialogHeader>
              <DialogTitle>Crop your {cropping.kind}</DialogTitle>
            </DialogHeader>
            <ImageCropper
              image={cropping.image}
              frameAspect={ASPECTS[cropping.kind]}
              round={cropping.kind === "avatar"}
              crop={cropping.crop}
              onCropChange={crop => setCropping(current => current && { ...current, crop })}
            />
            <DialogFooter>
              <Button variant="outline" onClick={discardCropping}>
                Back
              </Button>
              <Button onClick={apply}>Apply</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Profile images</DialogTitle>
            </DialogHeader>
            <ProfileImagesPreview
              profile={profile}
              avatar={avatar}
              banner={banner}
              onFile={(kind, file) => void pick(kind, file)}
            />
            <ProfileAppeals
              profileId={profile.profileId}
              inputKinds={["profile_avatar", "profile_banner"]}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
